import React, { useState, useEffect } from 'react';
import { getGlobalSettings } from '../utils/globalSettings';
import type { GlobalSettings } from '../utils/types';
import { keyboardManager } from '../utils/keyboardManager';
import { t } from '../utils/i18n';
import { toast } from '../hooks/useToast';

const GlobalSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<GlobalSettings>({
    closeModalOnOutsideClick: true,
    keyboardShortcut: 'Ctrl+I',
  });
  const [isLoading, setIsLoading] = useState(true);

  // 加载设置
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setIsLoading(true);
        const globalSettings = await getGlobalSettings();
        setSettings(globalSettings);
      } catch (error) {
        console.error('Failed to load settings:', error);
        toast.error('加载设置失败');
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, []);


  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* 头部 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {t('globalSettings')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            配置应用的全局设置和偏好
          </p>
        </div>

        {/* 设置面板 */}
        <div className="space-y-6">
          {/* 键盘快捷键设置 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">键盘快捷键</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                配置键盘快捷键以提高使用效率
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-900 dark:text-white">
                    打开提示词选择器
                  </label>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    使用快捷键快速打开提示词选择器
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded">
                    {keyboardManager.formatShortcutString(settings.keyboardShortcut)}
                  </kbd>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    (固定)
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 应用信息 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">应用信息</h2>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900 dark:text-white">版本</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">v1.0.0</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900 dark:text-white">应用名称</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">词匠工坊</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900 dark:text-white">存储类型</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">IndexedDB</span>
              </div>
            </div>
          </div>

          {/* 使用说明 */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-3">使用说明</h3>
            <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-2">
              <li>• 按 <kbd className="px-1 py-0.5 text-xs bg-blue-200 dark:bg-blue-800 rounded">Ctrl+I</kbd> 快速打开提示词选择器</li>
              <li>• 在提示词选择器中，您可以搜索、筛选和选择提示词</li>
              <li>• 选择提示词后，内容将自动复制到剪贴板</li>
              <li>• 所有数据都存储在本地浏览器中，确保隐私安全</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalSettingsPage;
